import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import DataGridWrapper from "../../../components/DataGridWrapper";
import { useState } from "react";
import useInstallmentSettingList from "./useInstallmentSettingList";

export const InstallmentSettingList = () => {
  const {
    settings,
    loading,
    totalCount,
    page,
    pageSize,
    searchParams,
    handlePageChange,
    handlePageSizeChange,
    handleSearch,
    handleGoToCreate,
    handleGoToEdit,
    handleDelete,
    handleRefresh,
  } = useInstallmentSettingList();

  const [searchForm, setSearchForm] = useState({
    search: searchParams.search || "",
    allowInstallment: searchParams.allowInstallment || "",
  });
  const [isInitialized, setIsInitialized] = useState(false);

  const handleSearchSubmit = () => {
    handleSearch(searchForm);
  };

  const handleSearchClear = () => {
    setSearchForm({ search: "", allowInstallment: "" });
    handleSearch({ search: "", allowInstallment: "" });
  };

  const columns: GridColDef[] = [
    {
      field: "settingId",
      headerName: "설정 ID",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "company",
      headerName: "도장명",
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.row.company?.comNm || "-"}
        </Typography>
      ),
    },
    {
      field: "product",
      headerName: "상품명",
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.product?.productNm || "-"}
        </Typography>
      ),
    },
    {
      field: "firstYearAmt",
      headerName: "연간 금액",
      width: 130,
      align: "right",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary.main">
          {params.row.product?.firstYearAmt
            ? `$${params.row.product.firstYearAmt.toLocaleString()}`
            : "-"}
        </Typography>
      ),
    },
    {
      field: "allowInstallment",
      headerName: "분할결제 허용",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.row.allowInstallment === "Y" ? "허용" : "비허용"}
          color={params.row.allowInstallment === "Y" ? "success" : "default"}
          size="small"
          variant={params.row.allowInstallment === "Y" ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "maxInstallmentCount",
      headerName: "최대 분할",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={`${params.row.maxInstallmentCount}회`}
          color="info"
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "minInstallmentAmount",
      headerName: "최소 분할 금액",
      width: 140,
      align: "right",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {`$${params.row.minInstallmentAmount.toLocaleString()}`}
        </Typography>
      ),
    },
    {
      field: "availableInstallments",
      headerName: "허용 분할 옵션",
      width: 180,
      renderCell: (params) => {
        try {
          const options = JSON.parse(params.row.availableInstallments);
          return (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {options.map((option: number, index: number) => (
                <Chip
                  key={index}
                  label={`${option}회`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              ))}
            </Box>
          );
        } catch {
          return <Typography variant="body2">-</Typography>;
        }
      },
    },
    {
      field: "regDttm",
      headerName: "등록일",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.row.regDttm).toLocaleDateString("ko-KR")}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "관리",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={0.5}
          width="100%"
          height="100%"
        >
          <Tooltip title="수정" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleGoToEdit(params.row.settingId)}
              sx={{
                width: 32,
                height: 32,
                "&:hover": {
                  backgroundColor: "primary.50",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.settingId)}
              sx={{
                width: 32,
                height: 32,
                "&:hover": {
                  backgroundColor: "error.50",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* 헤더 */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              color="text.primary"
              mb={1}
            >
              분할결제 설정 관리
            </Typography>
            <Typography variant="body2" color="text.secondary">
              도장별 분할결제 설정을 관리합니다
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: "medium",
              }}
            >
              새로고침
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleGoToCreate}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: "medium",
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              새 설정 추가
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 검색 필터 */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: 1,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="medium" mb={2}>
            검색 필터
          </Typography>
          <Grid container spacing={3} alignItems="end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="도장명/상품명 검색"
                value={searchForm.search}
                onChange={(e) =>
                  setSearchForm({ ...searchForm, search: e.target.value })
                }
                placeholder="검색어를 입력하세요"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="분할결제 허용 여부"
                value={searchForm.allowInstallment}
                onChange={(e) =>
                  setSearchForm({
                    ...searchForm,
                    allowInstallment: e.target.value,
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="Y">허용</MenuItem>
                <MenuItem value="N">비허용</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearchSubmit}
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: "medium",
                  }}
                >
                  검색
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSearchClear}
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: "medium",
                  }}
                >
                  초기화
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 데이터 그리드 */}
      <Card
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: 1,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <DataGridWrapper
            isReady={settings !== undefined && !loading}
            loadingMessage="분할결제 설정 데이터를 준비하는 중..."
            rows={settings || []}
            columns={columns}
            getRowId={(row) => row.settingId}
            loading={loading}
            paginationMode="server"
            rowCount={totalCount || 0}
            paginationModel={{
              page: Math.max(0, (page || 1) - 1),
              pageSize: pageSize || 10,
            }}
            onPaginationModelChange={(model) => {
              if (model.page !== (page || 1) - 1) {
                handlePageChange(model.page + 1);
              }
              if (model.pageSize !== pageSize) {
                handlePageSizeChange(model.pageSize);
              }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            autoHeight
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    flexDirection: "column",
                    gap: 1,
                    py: 4,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    표시할 분할결제 설정이 없습니다.
                  </Typography>
                </Box>
              ),
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e9ecef",
                "& .MuiDataGrid-columnHeader": {
                  fontWeight: "bold",
                  color: "text.primary",
                },
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#fdfdfd",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e9ecef",
                backgroundColor: "#f8f9fa",
              },
            }}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default InstallmentSettingList;
